// Catalog Shell - Flow Surface Pane
// A container pane that arranges child panes in a horizontal flow (infinite append)
// ============================================================================

import {
    PaneType,
    PaneInstance,
    PaneInstanceSpec,
    PaneTypeRenderContext,
    SurfaceAPI,
    FlowLayoutConfig,
    FlowPaneConfig,
    OpenPaneOptions,
    generateId,
} from '../core/types';
import { Shell } from '../core/shell';
import { createElement, clearElement } from '../utils/dom';
import { createButton } from '../utils/pane-ui';
import { icons } from '../utils/icons';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PANE_WIDTH = 750;
const MIN_PANE_WIDTH = 200;
const PANE_GAP = 2;
const COLLAPSED_WIDTH = 40;
const SHRINK_FACTOR = 0.8;
const STRETCH_FACTOR = 1.2;
// Width the trailing add-pane trigger takes out of the strip (keep in sync
// with .cs-flow-add-pane in styles/flow-surface.less).
const ADD_PANE_WIDTH = 32;

// ============================================================================
// Types
// ============================================================================

interface FlowPane {
    instance: PaneInstance;
    width: number;
    isCollapsed?: boolean;
    element?: HTMLElement;
    headerElement?: HTMLElement;
    contentElement?: HTMLElement;
}

interface ResizeState {
    paneIndex: number;
    startX: number;
    startWidth: number;
}

// ============================================================================
// Flow Surface Implementation
// ============================================================================

export class FlowSurface implements SurfaceAPI {
    public readonly surfaceId: string;
    public parentSurface?: SurfaceAPI;
    private shell: Shell;
    private instance: PaneInstance;
    private container: HTMLElement;

    // Panes in order (left to right)
    private panes: FlowPane[] = [];

    // DOM elements
    private flowContainer: HTMLElement | null = null;
    private panesWrapper: HTMLElement | null = null;

    // Resize state
    private resizeState: ResizeState | null = null;

    // Container width observer for automatic re-layout
    private containerObserver: ResizeObserver | null = null;
    private lastLayoutWidth: number = 0;

    // Trailing "+" trigger and the shared pane picker menu
    private addPaneElement: HTMLElement | null = null;
    private contextMenuElement: HTMLElement | null = null;

    // Active pane tracking
    private activePaneIndex: number = 0;
    /** Set by openPane to prevent the bubbling click from re-activating the origin pane */
    private skipNextPaneClick = false;

    constructor(shell: Shell, instance: PaneInstance, container: HTMLElement) {
        this.surfaceId = instance.instanceId;
        this.shell = shell;
        this.instance = instance;
        this.container = container;

        shell.registerSurface(this);
    }

    // ========================================================================
    // SurfaceAPI Implementation
    // ========================================================================

    getSurfaceType(): 'flow' {
        return 'flow';
    }

    getPanes(): PaneInstance[] {
        return this.panes.map(p => p.instance);
    }

    getActivePane(): PaneInstance | null {
        return this.panes[this.activePaneIndex]?.instance ?? null;
    }

    setActivePane(instanceId: string): void {
        const index = this.panes.findIndex(p => p.instance.instanceId === instanceId);
        if (index >= 0) {
            this.activePaneIndex = index;
            this.updateActiveStyles();
            this.layout();
            this.shell.setFocusedPane(instanceId, this.surfaceId);
            this.shell.saveConfiguration();
        }
    }

    openPane(spec: PaneInstanceSpec, options?: OpenPaneOptions): PaneInstance {
        const instance = this.shell.getTypeRegistry().createInstance(spec);
        instance.parentSurfaceId = this.surfaceId;
        instance.isDynamic = true;
        this.shell.registerPaneInstance(instance);

        const paneType = this.shell.getPaneType(instance.typeId);
        const flowPane: FlowPane = {
            instance,
            width: paneType?.defaultWidth ?? DEFAULT_PANE_WIDTH,
        };

        // Determine insertion position
        let insertIndex = this.panes.length;

        if (options?.originPaneId) {
            // Insert after the origin pane
            const originIndex = this.panes.findIndex(p => p.instance.instanceId === options.originPaneId);
            if (originIndex >= 0) {
                insertIndex = originIndex + 1;

                // Cascade close: remove all panes to the right of origin (incremental)
                this.removePanesFromIndex(insertIndex);
            }
        }

        this.panes.push(flowPane);

        if (options?.activate !== false) {
            this.activePaneIndex = this.panes.length - 1;
        }

        // Incremental append instead of full re-render
        // This preserves existing panes' DOM state (important for VirtualScroller, etc.)
        if (this.panesWrapper) {
            this.appendPaneToDOM(this.panes.length - 1);
            this.updateActiveStyles();
            this.layout();
        } else {
            // Fallback: full render if not yet initialized
            this.render();
        }

        if (options?.activate !== false) {
            this.skipNextPaneClick = true;
            this.shell.setFocusedPane(instance.instanceId, this.surfaceId);
        }

        this.shell.emit('pane-opened', { instanceId: instance.instanceId, surfaceId: this.surfaceId });
        this.shell.saveConfiguration();
        return instance;
    }

    /**
     * Remove panes from a given index onwards (incremental removal).
     * Does not trigger full re-render.
     */
    private removePanesFromIndex(fromIndex: number): void {
        while (this.panes.length > fromIndex) {
            const removed = this.panes.pop()!;

            // Remove from DOM
            if (removed.element && removed.element.parentNode) {
                removed.element.remove();
            }

            this.shell.unregisterPaneInstance(removed.instance.instanceId);
            this.shell.emit('pane-closed', {
                instanceId: removed.instance.instanceId,
                surfaceId: this.surfaceId,
            });
        }
    }

    /**
     * Remove a single pane at the given index, leaving panes to its right in
     * place (incremental). The counterpart to {@link removePanesFromIndex},
     * used when the surface opts out of cascade-close (see closePane).
     */
    private removePaneAt(index: number): void {
        const removed = this.panes[index];
        if (!removed) return;
        this.panes.splice(index, 1);
        if (removed.element && removed.element.parentNode) {
            removed.element.remove();
        }
        this.shell.unregisterPaneInstance(removed.instance.instanceId);
        this.shell.emit('pane-closed', {
            instanceId: removed.instance.instanceId,
            surfaceId: this.surfaceId,
        });
    }

    /**
     * Append a single pane to the DOM without re-rendering others.
     */
    private appendPaneToDOM(index: number): void {
        const pane = this.panes[index];
        if (!this.panesWrapper || !pane) return;

        const isActive = index === this.activePaneIndex;
        const isFirst = index === 0;

        // Pane container
        pane.element = createElement('div', {
            className: `cs-flow-pane ${isActive ? 'cs-active' : ''}`,
            attributes: {
                'data-pane-id': pane.instance.instanceId,
            },
        });
        pane.element.style.width = `${pane.width}px`;
        pane.element.style.flexShrink = '0';

        // Header with title and close button
        pane.headerElement = createElement('div', {
            className: 'cs-flow-pane-header',
        });

        // Pane title. The row does not wrap, so the title is the element that
        // gives way (`flex: 0 1 auto; min-width: 0` in the stylesheet) and
        // ellipsizes; the `title` attribute is how a truncated one can still
        // be read.
        const title = createElement('div', {
            className: 'cs-flow-pane-title',
            textContent: pane.instance.title,
            attributes: { title: pane.instance.title },
        });
        pane.headerElement.appendChild(title);

        // Spacer
        const spacer = createElement('div', { className: 'cs-flow-pane-spacer' });
        pane.headerElement.appendChild(spacer);

        // Pane-type tab bar actions (right-aligned, before system buttons)
        {
            const paneType = this.shell.getPaneType(pane.instance.typeId);
            const actions = paneType?.tabBarActions?.(pane.instance);
            if (actions && actions.length > 0) {
                const actionsContainer = createElement('div', { className: 'cs-tab-bar-actions' });
                for (const action of actions) actionsContainer.appendChild(action);
                pane.headerElement.appendChild(actionsContainer);
            }
        }

        // Popout button (hidden if already in a popout window)
        if (!this.parentSurface) {
            const paneType = this.shell.getPaneType(pane.instance.typeId);
            if (paneType?.canCopyToPopup !== false) {
                const popoutBtn = createButton({
                    icon: icons.popout(),
                    onClick: (e) => {
                        e.stopPropagation();
                        const rect = pane.element?.getBoundingClientRect();
                        const sourceSize = rect && rect.width > 0 && rect.height > 0
                            ? { width: Math.round(rect.width), height: Math.round(rect.height) }
                            : undefined;
                        (this.shell as Shell).getPopoutManager().popoutPane(pane.instance, this, sourceSize);
                    },
                });
                popoutBtn.title = 'Pop out';
                pane.headerElement.appendChild(popoutBtn);
            }
        }

        // Close button (not shown for first pane - it cannot be reopened)
        if (!isFirst) {
            const closeBtn = createButton({
                icon: icons.close(),
                onClick: async (e) => {
                    e.stopPropagation();
                    if (await this.shouldClosePane(pane.instance)) {
                        this.closePane(pane.instance.instanceId);
                    }
                },
            });
            closeBtn.title = 'Close';
            pane.headerElement.appendChild(closeBtn);
        }

        pane.element.appendChild(pane.headerElement);

        // Content area
        pane.contentElement = createElement('div', {
            className: 'cs-flow-pane-content',
        });

        // Render pane content
        const type = this.shell.getPaneType(pane.instance.typeId);
        if (type) {
            // Add fill modifier if pane type has 'fill' capability
            if (type.capabilities.includes('fill')) {
                pane.contentElement.classList.add('cs-flow-pane-content--fill');
            }

            let element = pane.instance.element;
            if (!element) {
                element = type.render(this.shell.createRenderContext(pane.instance, this));
                pane.instance.element = element;
            }
            pane.contentElement.appendChild(element);
        }

        pane.element.appendChild(pane.contentElement);

        // Resize handle (on right edge)
        const resizeHandle = createElement('div', {
            className: 'cs-flow-pane-resize-handle',
        });
        resizeHandle.addEventListener('mousedown', (e) => this.startResize(index, e));
        pane.element.appendChild(resizeHandle);

        // Click to activate
        pane.element.addEventListener('click', () => {
            if (this.skipNextPaneClick) {
                this.skipNextPaneClick = false;
                return;
            }
            this.setActivePane(pane.instance.instanceId);
        });

        this.panesWrapper.appendChild(pane.element);
        this.ensureAddPaneTrigger();
    }

    closePane(instanceId: string): void {
        const index = this.panes.findIndex(p => p.instance.instanceId === instanceId);
        if (index <= 0) return;

        // Cascade-close (remove this pane and everything to its right) is the
        // default — it gives the Library its Miller-columns peek. A surface can
        // opt out via `context.cascadeCloseRight === false` (the agent session
        // surface does, so a chat pane's subagents close independently).
        if (this.instance.context.cascadeCloseRight === false) {
            this.removePaneAt(index);
            if (this.activePaneIndex > index) this.activePaneIndex -= 1;
        } else {
            this.removePanesFromIndex(index);
        }

        // Adjust active index
        if (this.activePaneIndex >= this.panes.length) {
            this.activePaneIndex = Math.max(0, this.panes.length - 1);
        }

        this.updateActiveStyles();
        this.layout();
        this.ensureAddPaneTrigger();

        // Notify shell that the remaining pane is now focused
        const activePane = this.panes[this.activePaneIndex];
        if (activePane) {
            this.shell.setFocusedPane(activePane.instance.instanceId, this.surfaceId);
        }

        this.shell.saveConfiguration();
    }

    getPaneById(instanceId: string): PaneInstance | null {
        const found = this.panes.find(p => p.instance.instanceId === instanceId);
        return found?.instance ?? null;
    }

    /**
     * Run the pane type's confirmClose guard (if any) ahead of a user-initiated
     * close. Returns true when the close should proceed.
     */
    private async shouldClosePane(instance: PaneInstance): Promise<boolean> {
        const type = this.shell.getPaneType(instance.typeId);
        if (type?.confirmClose) {
            return await type.confirmClose(instance);
        }
        return true;
    }

    updatePaneTitle(instanceId: string, title: string): void {
        const pane = this.panes.find(p => p.instance.instanceId === instanceId);
        if (!pane) return;
        pane.instance.title = title;
        const titleEl = pane.headerElement?.querySelector('.cs-flow-pane-title');
        if (titleEl) {
            titleEl.textContent = title;
            // Keep the tooltip in step: the title is the one element in the
            // row that truncates.
            titleEl.setAttribute('title', title);
        }
    }

    /**
     * Re-run a pane type's `tabBarActions` and swap them into the header in
     * place — lets a pane refresh its tab-bar buttons when its underlying state
     * changes (e.g. the session summary switching continue/close for
     * reopen/delete when its session closes) without a full pane re-render.
     */
    refreshPaneActions(instanceId: string): void {
        const pane = this.panes.find(p => p.instance.instanceId === instanceId);
        if (!pane?.headerElement) return;
        const paneType = this.shell.getPaneType(pane.instance.typeId);
        const actions = paneType?.tabBarActions?.(pane.instance) ?? [];
        let container = pane.headerElement.querySelector<HTMLElement>('.cs-tab-bar-actions');
        if (!container) {
            if (actions.length === 0) return;
            // Sits between the spacer and the system buttons (popout/close).
            container = createElement('div', { className: 'cs-tab-bar-actions' });
            const spacer = pane.headerElement.querySelector('.cs-flow-pane-spacer');
            if (spacer) spacer.after(container);
            else pane.headerElement.appendChild(container);
        }
        clearElement(container);
        for (const action of actions) container.appendChild(action);
    }

    // ========================================================================
    // Add-Pane Picker
    // ========================================================================

    /**
     * The pane types this surface offers: the active workspace's
     * `pickerPanes`, narrowed to registered LEAF types. The picker is opt-in —
     * a workspace that declares no `pickerPanes` offers nothing, because the
     * picker opens a type with NO `context` and a record pane opened that way
     * renders empty. (`recommendedPanes` is deliberately not read here: it is
     * a highlight-and-seed list, and modules put record panes in it.)
     */
    private getPickerRoster(): PaneType[] {
        const workspace = this.shell.getWorkspaceDef(this.shell.getActiveWorkspace());
        const roster: PaneType[] = [];
        for (const typeId of workspace?.pickerPanes ?? []) {
            const type = this.shell.getPaneType(typeId);
            if (type && type.kind === 'leaf' && !roster.includes(type)) roster.push(type);
        }
        return roster;
    }

    /**
     * Whether this surface offers the picker at all. The roster comes from the
     * WORKSPACE, so it belongs to the surface that IS the workspace — its root
     * — and to popout windows of it (a popout is the same workspace in another
     * window, and a pane opened there lands next to what the user is actually
     * looking at). A flow surface nested inside another surface has no roster
     * of its own and offers nothing.
     */
    private offersPanePicker(): boolean {
        return this.parentSurface !== undefined || !this.instance.parentSurfaceId;
    }

    /**
     * Create (once) the trailing "+" trigger and move it back to the end of the
     * strip. Every path that adds or removes a pane calls this, because a pane
     * is appended to the same wrapper and would otherwise sit after it.
     */
    private ensureAddPaneTrigger(): void {
        if (!this.panesWrapper) return;
        if (!this.addPaneElement) {
            this.addPaneElement = createElement('button', {
                className: 'cs-flow-add-pane',
                textContent: '+',
                attributes: { type: 'button', title: 'Open pane', 'aria-label': 'Open pane' },
                onClick: (e) => {
                    e.stopPropagation();
                    this.showPanePicker(e);
                },
            });
        }
        this.addPaneElement.hidden = !this.offersPanePicker() || this.getPickerRoster().length === 0;
        this.panesWrapper.appendChild(this.addPaneElement);
    }

    /**
     * The picker both triggers share. Mirrors the grid surface's context menu
     * (same `.cs-context-menu` markup, positioning and outside-click close) so
     * the two surfaces look alike; a ✓ marks a type already open here.
     */
    private showPanePicker(e: MouseEvent): void {
        e.preventDefault();
        this.hideContextMenu();

        if (!this.offersPanePicker()) return;
        const roster = this.getPickerRoster();
        if (roster.length === 0) return;

        const menu = createElement('div', { className: 'cs-context-menu' });
        for (const type of roster) {
            const isOpen = this.panes.some(p => p.instance.typeId === type.typeId);
            const item = createElement('div', {
                className: 'cs-menu-item',
                onClick: (ev) => {
                    ev.stopPropagation();
                    this.hideContextMenu();
                    this.openOrActivatePaneType(type.typeId);
                },
            });
            item.appendChild(createElement('span', {
                className: `cs-menu-icon ${isOpen ? 'cs-menu-icon-active' : 'cs-menu-icon-none'}`,
                textContent: isOpen ? '✓' : ' ',
            }));
            item.appendChild(createElement('span', {
                className: 'cs-menu-label',
                textContent: type.defaultTitle,
            }));
            menu.appendChild(item);
        }

        menu.style.position = 'fixed';
        menu.style.left = `${e.clientX}px`;
        menu.style.top = `${e.clientY}px`;
        // A popout surface lives in another window's document; the menu (and
        // the outside-click listener that closes it) must go there, not into
        // the main window where nobody would see it. appendChild adopts the
        // node, so building it with the main document is fine.
        const doc = this.container.ownerDocument ?? document;
        doc.body.appendChild(menu);
        this.contextMenuElement = menu;

        setTimeout(() => {
            doc.addEventListener('click', this.handleContextMenuClose);
        }, 0);
    }

    /**
     * A type already open in this surface is scrolled to and focused — never
     * opened twice. Anything else opens after the focused pane (flow's
     * open-beside-the-origin rule), or at the end when the focus is elsewhere.
     */
    private openOrActivatePaneType(typeId: string): void {
        const index = this.panes.findIndex(p => p.instance.typeId === typeId);
        if (index >= 0) {
            this.setActivePane(this.panes[index].instance.instanceId);
            this.scrollToPane(index, true);
            return;
        }

        const focused = this.shell.getFocusedPane();
        const originPaneId = focused && this.panes.some(p => p.instance.instanceId === focused.instanceId)
            ? focused.instanceId
            : undefined;
        this.openPane({ typeId }, { originPaneId, activate: true });
    }

    private handleContextMenuClose = (): void => {
        this.hideContextMenu();
    };

    private hideContextMenu(): void {
        if (this.contextMenuElement) {
            const doc = this.contextMenuElement.ownerDocument ?? document;
            this.contextMenuElement.remove();
            this.contextMenuElement = null;
            doc.removeEventListener('click', this.handleContextMenuClose);
        }
    }

    // ========================================================================
    // Additional Flow Methods
    // ========================================================================

    /**
     * Open a new pane to the right of an existing pane
     */
    openFromPane(originPaneId: string, spec: PaneInstanceSpec): PaneInstance {
        return this.openPane(spec, { originPaneId, activate: true });
    }

    /**
     * Close all panes to the right of (and including) the specified pane
     */
    closePanesAfter(paneId: string): void {
        const index = this.panes.findIndex(p => p.instance.instanceId === paneId);
        if (index < 0) return;

        // Incremental removal
        this.removePanesFromIndex(index);

        // Adjust active index
        if (this.activePaneIndex >= this.panes.length) {
            this.activePaneIndex = Math.max(0, this.panes.length - 1);
        }

        this.updateActiveStyles();
        this.layout();
    }

    private closeAllPanes(): void {
        // Close all panes (remove from DOM and unregister)
        this.removePanesFromIndex(0);

        // Signal that surface should be closed
        this.shell.emit('layout-changed', {
            surfaceId: this.surfaceId,
            action: 'close-surface',
        });
    }

    // ========================================================================
    // Layout Initialization
    // ========================================================================

    initializeLayout(config: FlowLayoutConfig): void {
        this.panes = [];

        for (const paneConfig of config.panes) {
            // If the pane spec has a nested layout config, pass it via context
            // so that container panes can restore their internal layout
            const paneSpec = { ...paneConfig.paneSpec };
            if (paneConfig.paneSpec.nestedLayoutConfig) {
                paneSpec.context = {
                    ...paneConfig.paneSpec.context,
                    __nestedLayoutConfig: paneConfig.paneSpec.nestedLayoutConfig,
                };
            }

            const instance = this.shell.getTypeRegistry().createInstance(paneSpec);
            instance.parentSurfaceId = this.surfaceId;
            this.shell.registerPaneInstance(instance);

            this.panes.push({
                instance,
                width: paneConfig.width ?? DEFAULT_PANE_WIDTH,
            });
        }

        if (this.panes.length > 0) {
            // Restore active pane index (clamped to valid range)
            const restoredIndex = config.activePaneIndex ?? 0;
            this.activePaneIndex = Math.min(restoredIndex, this.panes.length - 1);
        }

        this.render();

        // Set focus on the restored active pane and scroll to it
        if (this.panes.length > 0) {
            const activePane = this.panes[this.activePaneIndex];
            this.shell.setFocusedPane(activePane.instance.instanceId, this.surfaceId);
            requestAnimationFrame(() => {
                this.scrollToPane(this.activePaneIndex);
            });
        }
    }

    // ========================================================================
    // Rendering
    // ========================================================================

    render(): void {
        clearElement(this.container);

        this.flowContainer = createElement('div', {
            className: 'cs-flow-surface',
        });

        this.panesWrapper = createElement('div', {
            className: 'cs-flow-panes-wrapper',
        });

        for (let i = 0; i < this.panes.length; i++) {
            this.renderPane(i);
        }

        this.ensureAddPaneTrigger();

        // Right-click on the surface itself (never on a pane, which keeps its
        // own menu) is the second trigger for the same picker.
        this.flowContainer.addEventListener('contextmenu', (e) => {
            if ((e.target as Element | null)?.closest('.cs-flow-pane')) return;
            this.showPanePicker(e);
        });

        this.flowContainer.appendChild(this.panesWrapper);
        this.container.appendChild(this.flowContainer);

        this.updateActiveStyles();
        this.installContainerObserver();
        this.layout();
    }

    private installContainerObserver(): void {
        if (this.containerObserver || !this.panesWrapper) return;
        const target = this.panesWrapper;
        this.containerObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect.width;
                if (Math.abs(w - this.lastLayoutWidth) >= 1) {
                    this.layout();
                }
            }
        });
        this.containerObserver.observe(target);
    }

    /**
     * Compute and apply pane widths + collapsed state to fit available width.
     *
     * Rules:
     *  - Active pane is always shown in full (never collapsed).
     *  - If totals exceed available width, collapse leftmost non-active panes one at a time
     *    until the rest fit. Collapsed panes occupy COLLAPSED_WIDTH.
     *  - Distribute any remaining slack: shrinkable panes grow up to ideal·1.2, then any
     *    `stretch` pane absorbs the rest.
     *  - Panes are never partially displayed: either full (at/above min) or collapsed.
     */
    private layout(): void {
        if (!this.panesWrapper || this.panes.length === 0) return;

        // clientWidth includes wrapper padding; flex items live in the content box,
        // so subtract horizontal padding to get the width actually available to panes.
        const cs = getComputedStyle(this.panesWrapper);
        const padL = parseFloat(cs.paddingLeft) || 0;
        const padR = parseFloat(cs.paddingRight) || 0;
        const addTriggerWidth = this.addPaneElement && !this.addPaneElement.hidden
            ? ADD_PANE_WIDTH + PANE_GAP
            : 0;
        const available = this.panesWrapper.clientWidth - padL - padR - addTriggerWidth;
        this.lastLayoutWidth = available;
        if (available <= 0) return;

        // On compact screens, collapsed panes are hidden entirely (no tab strip) —
        // the active pane should fill the whole viewport.
        const hideCollapsed = this.shell.getScreenSize() === 'compact';
        const collapsedWidth = hideCollapsed ? 0 : COLLAPSED_WIDTH;

        const N = this.panes.length;

        interface Meta {
            ideal: number;
            min: number;
            max: number;
            stretch: boolean;
            collapsed: boolean;
        }

        const metas: Meta[] = this.panes.map((p) => {
            const type = this.shell.getPaneType(p.instance.typeId);
            const ideal = Math.max(MIN_PANE_WIDTH, p.width || type?.defaultWidth || DEFAULT_PANE_WIDTH);
            const canShrink = type?.canShrink !== false;
            const stretch = type?.stretch === true;
            const min = canShrink ? Math.max(MIN_PANE_WIDTH, ideal * SHRINK_FACTOR) : ideal;
            const max = stretch ? Number.POSITIVE_INFINITY : (canShrink ? ideal * STRETCH_FACTOR : ideal);
            return { ideal, min, max, stretch, collapsed: false };
        });

        // When a collapsed pane is hidden, it is not a flex item and contributes no gap.
        const visibleCount = (): number => {
            if (!hideCollapsed) return N;
            let c = 0;
            for (let i = 0; i < N; i++) if (!metas[i].collapsed) c++;
            return c;
        };

        const totalMin = (): number => {
            let sum = 0;
            for (let i = 0; i < N; i++) sum += metas[i].collapsed ? collapsedWidth : metas[i].min;
            return sum + PANE_GAP * Math.max(0, visibleCount() - 1);
        };

        // On compact screens, only the active pane is shown. Everything else is
        // hidden so the active pane can take the full viewport width regardless
        // of its ideal width.
        if (hideCollapsed) {
            for (let i = 0; i < N; i++) {
                if (i !== this.activePaneIndex) metas[i].collapsed = true;
            }
            if (metas[this.activePaneIndex]) {
                metas[this.activePaneIndex].max = Number.POSITIVE_INFINITY;
                metas[this.activePaneIndex].stretch = true;
            }
        }

        // Collapse leftmost non-active until we fit.
        while (totalMin() > available) {
            let collapsed = false;
            for (let i = 0; i < N; i++) {
                if (!metas[i].collapsed && i !== this.activePaneIndex) {
                    metas[i].collapsed = true;
                    collapsed = true;
                    break;
                }
            }
            if (!collapsed) break; // only active pane left
        }

        // Start widths at min.
        const widths: number[] = metas.map(m => m.collapsed ? collapsedWidth : m.min);
        const finalGaps = PANE_GAP * Math.max(0, visibleCount() - 1);
        let remaining = available - widths.reduce((a, b) => a + b, 0) - finalGaps;

        if (remaining > 0) {
            // Grow non-stretch, non-collapsed panes up to their max (ideal·1.2 by default).
            let eligible: number[] = [];
            for (let i = 0; i < N; i++) {
                if (!metas[i].collapsed && !metas[i].stretch && widths[i] < metas[i].max) {
                    eligible.push(i);
                }
            }
            let guard = 0;
            while (remaining > 0.5 && eligible.length > 0 && guard++ < 8) {
                const rooms = eligible.map(i => metas[i].max - widths[i]);
                const totalRoom = rooms.reduce((a, b) => a + b, 0);
                if (totalRoom <= 0.5) break;
                const factor = Math.min(1, remaining / totalRoom);
                const next: number[] = [];
                for (let k = 0; k < eligible.length; k++) {
                    const i = eligible[k];
                    const add = rooms[k] * factor;
                    widths[i] += add;
                    remaining -= add;
                    if (metas[i].max - widths[i] > 0.5) next.push(i);
                }
                if (factor >= 1 - 1e-6) break;
                eligible = next;
            }

            // Stretch panes soak up the rest.
            const stretchIdx: number[] = [];
            for (let i = 0; i < N; i++) {
                if (!metas[i].collapsed && metas[i].stretch) stretchIdx.push(i);
            }
            if (remaining > 0.5 && stretchIdx.length > 0) {
                const share = remaining / stretchIdx.length;
                for (const i of stretchIdx) widths[i] += share;
                remaining = 0;
            }
        } else if (remaining < 0) {
            // Only active pane visible and even its min doesn't fit. Let it take what's left.
            for (let i = 0; i < N; i++) {
                if (!metas[i].collapsed) {
                    widths[i] = Math.max(MIN_PANE_WIDTH / 2, widths[i] + remaining);
                    break;
                }
            }
        }

        for (let i = 0; i < N; i++) {
            const pane = this.panes[i];
            pane.isCollapsed = metas[i].collapsed;
            if (pane.element) {
                const hidden = metas[i].collapsed && hideCollapsed;
                pane.element.style.display = hidden ? 'none' : '';
                if (!hidden) {
                    const w = Math.round(widths[i]);
                    pane.element.style.width = `${w}px`;
                }
                pane.element.style.minWidth = '';
                pane.element.style.flexShrink = '0';
                pane.element.classList.toggle('cs-collapsed', metas[i].collapsed && !hideCollapsed);
            }
        }
    }

    private renderPane(index: number): void {
        const pane = this.panes[index];
        if (!this.panesWrapper) return;

        const isActive = index === this.activePaneIndex;
        const isFirst = index === 0;

        // Pane container
        pane.element = createElement('div', {
            className: `cs-flow-pane ${isActive ? 'cs-active' : ''}`,
            attributes: {
                'data-pane-id': pane.instance.instanceId,
            },
        });
        pane.element.style.width = `${pane.width}px`;
        pane.element.style.flexShrink = '0';

        // Header with title and close button
        pane.headerElement = createElement('div', {
            className: 'cs-flow-pane-header',
        });

        // Pane title. The row does not wrap, so the title is the element that
        // gives way (`flex: 0 1 auto; min-width: 0` in the stylesheet) and
        // ellipsizes; the `title` attribute is how a truncated one can still
        // be read.
        const title = createElement('div', {
            className: 'cs-flow-pane-title',
            textContent: pane.instance.title,
            attributes: { title: pane.instance.title },
        });
        pane.headerElement.appendChild(title);

        // Spacer
        const spacer = createElement('div', { className: 'cs-flow-pane-spacer' });
        pane.headerElement.appendChild(spacer);

        // Pane-type tab bar actions (right-aligned, before system buttons)
        {
            const paneType = this.shell.getPaneType(pane.instance.typeId);
            const actions = paneType?.tabBarActions?.(pane.instance);
            if (actions && actions.length > 0) {
                const actionsContainer = createElement('div', { className: 'cs-tab-bar-actions' });
                for (const action of actions) actionsContainer.appendChild(action);
                pane.headerElement.appendChild(actionsContainer);
            }
        }

        // Popout button (hidden if already in a popout window)
        if (!this.parentSurface) {
            const paneType = this.shell.getPaneType(pane.instance.typeId);
            if (paneType?.canCopyToPopup !== false) {
                const popoutBtn = createButton({
                    icon: icons.popout(),
                    onClick: (e) => {
                        e.stopPropagation();
                        const rect = pane.element?.getBoundingClientRect();
                        const sourceSize = rect && rect.width > 0 && rect.height > 0
                            ? { width: Math.round(rect.width), height: Math.round(rect.height) }
                            : undefined;
                        (this.shell as Shell).getPopoutManager().popoutPane(pane.instance, this, sourceSize);
                    },
                });
                popoutBtn.title = 'Pop out';
                pane.headerElement.appendChild(popoutBtn);
            }
        }

        // Close button (not shown for first pane - it cannot be reopened)
        if (!isFirst) {
            const closeBtn = createButton({
                icon: icons.close(),
                onClick: async (e) => {
                    e.stopPropagation();
                    if (await this.shouldClosePane(pane.instance)) {
                        this.closePane(pane.instance.instanceId);
                    }
                },
            });
            closeBtn.title = 'Close';
            pane.headerElement.appendChild(closeBtn);
        }

        pane.element.appendChild(pane.headerElement);

        // Content area
        pane.contentElement = createElement('div', {
            className: 'cs-flow-pane-content',
        });

        // Render pane content
        const type = this.shell.getPaneType(pane.instance.typeId);
        if (type) {
            // Add fill modifier if pane type has 'fill' capability
            // This enables panes that handle their own scrolling (e.g., DataListView)
            if (type.capabilities.includes('fill')) {
                pane.contentElement.classList.add('cs-flow-pane-content--fill');
            }

            let element = pane.instance.element;
            if (!element) {
                element = type.render(this.shell.createRenderContext(pane.instance, this));
                pane.instance.element = element;
            }
            pane.contentElement.appendChild(element);
        }

        pane.element.appendChild(pane.contentElement);

        // Resize handle (on right edge)
        const resizeHandle = createElement('div', {
            className: 'cs-flow-pane-resize-handle',
        });
        resizeHandle.addEventListener('mousedown', (e) => this.startResize(index, e));
        pane.element.appendChild(resizeHandle);

        // Click to activate
        pane.element.addEventListener('click', () => {
            if (this.skipNextPaneClick) {
                this.skipNextPaneClick = false;
                return;
            }
            this.setActivePane(pane.instance.instanceId);
        });

        this.panesWrapper.appendChild(pane.element);
        this.ensureAddPaneTrigger();
    }

    private updateActiveStyles(): void {
        for (let i = 0; i < this.panes.length; i++) {
            const pane = this.panes[i];
            if (pane.element) {
                pane.element.classList.toggle('cs-active', i === this.activePaneIndex);
            }
        }
    }

    private scrollToPane(index: number, animate: boolean = false): void {
        const pane = this.panes[index];
        if (!pane?.element || !this.panesWrapper) return;

        const containerWidth = this.panesWrapper.clientWidth;
        const scrollLeft = this.panesWrapper.scrollLeft;
        const paneLeft = pane.element.offsetLeft;
        const paneWidth = pane.element.offsetWidth;
        const paneRight = paneLeft + paneWidth;

        // Check if pane is already fully visible
        const visibleLeft = scrollLeft;
        const visibleRight = scrollLeft + containerWidth;
        const isFullyVisible = paneLeft >= visibleLeft && paneRight <= visibleRight;

        // Don't scroll if pane is already fully visible (prevents layout shift on click)
        if (isFullyVisible) return;

        // Calculate position to center the pane
        const scrollTarget = paneLeft - (containerWidth - paneWidth) / 2;

        if (animate) {
            this.panesWrapper.scrollTo({
                left: Math.max(0, scrollTarget),
                behavior: 'smooth',
            });
        } else {
            this.panesWrapper.scrollLeft = Math.max(0, scrollTarget);
        }
    }

    // ========================================================================
    // Resize Handling
    // ========================================================================

    private startResize(paneIndex: number, e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();

        const pane = this.panes[paneIndex];
        if (!pane) return;

        this.resizeState = {
            paneIndex,
            startX: e.clientX,
            startWidth: pane.width,
        };

        document.addEventListener('mousemove', this.handleResizeMove);
        document.addEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = 'ew-resize';
    }

    private handleResizeMove = (e: MouseEvent): void => {
        if (!this.resizeState) return;

        const delta = e.clientX - this.resizeState.startX;
        const newWidth = Math.max(MIN_PANE_WIDTH, this.resizeState.startWidth + delta);

        const pane = this.panes[this.resizeState.paneIndex];
        if (pane) {
            pane.width = newWidth;
            this.layout();
        }
    };

    private handleResizeEnd = (): void => {
        this.resizeState = null;
        document.removeEventListener('mousemove', this.handleResizeMove);
        document.removeEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = '';

        this.shell.saveConfiguration();
    };

    // ========================================================================
    // Export Layout Config
    // ========================================================================

    getLayoutConfig(): FlowLayoutConfig {
        // Ephemeral panes (e.g. the agent's transient note/commit editor) are left
        // out of the saved layout, so a reload doesn't restore them as empty shells.
        const savable = this.panes.filter(
            p => !this.shell.getPaneType(p.instance.typeId)?.capabilities.includes('ephemeral'),
        );
        // The active pane may have been an ephemeral one that's now gone; clamp.
        const activeIdx = savable.indexOf(this.panes[this.activePaneIndex]);
        return {
            type: 'flow',
            activePaneIndex: activeIdx >= 0 ? activeIdx : Math.max(0, savable.length - 1),
            panes: savable.map(p => {
                const paneSpec: PaneInstanceSpec = {
                    typeId: p.instance.typeId,
                    title: p.instance.title,
                    context: p.instance.context,
                    flags: p.instance.flags,
                    isDynamic: p.instance.isDynamic || undefined,
                };

                // For container panes, recursively capture their internal layout
                const paneType = this.shell.getPaneType(p.instance.typeId);
                if (paneType && paneType.kind === 'container') {
                    const nestedSurface = this.shell.getSurface(p.instance.instanceId);
                    if (nestedSurface && 'getLayoutConfig' in nestedSurface) {
                        paneSpec.nestedLayoutConfig = (nestedSurface as FlowSurface).getLayoutConfig();
                    }
                }

                return {
                    paneSpec,
                    width: p.width,
                };
            }),
        };
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    destroy(): void {
        this.hideContextMenu();
        this.addPaneElement = null;
        if (this.containerObserver) {
            this.containerObserver.disconnect();
            this.containerObserver = null;
        }

        // Unregister all panes
        for (const pane of this.panes) {
            this.shell.unregisterPaneInstance(pane.instance.instanceId);
        }

        // Unregister surface
        this.shell.unregisterSurface(this.surfaceId);

        // Clear DOM
        clearElement(this.container);
    }
}

// ============================================================================
// Flow Surface Pane Type
// ============================================================================

export function createFlowSurfacePaneType(): PaneType {
    return {
        typeId: 'flow-surface',
        kind: 'container',
        containerLayout: 'flow',
        defaultTitle: 'Flow Surface',
        capabilities: ['host'],
        defaultFlags: {
            reparentable: false,
            cloneable: false,
        },
        render: (ctx: PaneTypeRenderContext): HTMLElement => {
            const container = createElement('div', {
                className: 'cs-flow-surface-container',
            });

            // Create the FlowSurface instance
            const shell = ctx.shell as Shell;
            const surface = new FlowSurface(
                shell,
                ctx.instance,
                container
            );

            // Tear the surface (and its child panes) down when this root pane is
            // unregistered — i.e. when the workspace is closed. removeWorkspace
            // only unregisters the root pane + drops the surface record, so
            // without this the child leaves' disposables/onDestroy never run
            // (they leak — e.g. a chat pane's runtime socket stays open).
            ctx.disposables.add(() => surface.destroy());

            // Check for stored layout config (persisted from previous session)
            const workspaceId = shell.getActiveWorkspace();
            const storedLayout = shell.getStoredLayoutConfig(workspaceId);

            if (storedLayout && storedLayout.type === 'flow') {
                // Restore from stored layout
                surface.initializeLayout(storedLayout as FlowLayoutConfig);
            } else {
                // Check for workspace's rootLayoutConfig
                const workspace = shell.getWorkspaceDef(workspaceId);

                if (workspace?.rootLayoutConfig && workspace.rootLayoutConfig.type === 'flow') {
                    // Use workspace's default layout
                    surface.initializeLayout(workspace.rootLayoutConfig as FlowLayoutConfig);
                } else {
                    // Fall back to recommended panes or empty layout
                    const recommendedPanes = workspace?.recommendedPanes ?? [];
                    const initialPanes: FlowPaneConfig[] = [];

                    // Add first recommended leaf pane
                    for (const typeId of recommendedPanes) {
                        const paneType = shell.getPaneType(typeId);
                        if (paneType && paneType.kind === 'leaf') {
                            initialPanes.push({ paneSpec: { typeId } });
                            break;  // Only add the first one for flow surfaces
                        }
                    }

                    const defaultConfig: FlowLayoutConfig = {
                        type: 'flow',
                        panes: initialPanes,
                    };
                    surface.initializeLayout(defaultConfig);
                }
            }

            return container;
        },
    };
}
